import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Upload } from "lucide-react";
import { rhm, apiError } from "../api";
import { Btn } from "../ui";
import { MediaLibrary } from "../pages/MediaLibrary";

const CHUNK = 5 * 1024 * 1024;
const randomId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

/** Big guides go up in 5 MB pieces so the proxy never sees one oversized request. */
export const uploadDigitalFile = async (productId, file, onProgress) => {
    const uploadId = randomId();
    const total = Math.ceil(file.size / CHUNK);
    for (let i = 0; i < total; i += 1) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", String(i));
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), file.name);
        await rhm.post(`/digital/${productId}/file/chunk`, fd);
        onProgress?.(Math.round(((i + 1) / total) * 100));
    }
    await rhm.post(`/digital/${productId}/file/finish`, {
        upload_id: uploadId, filename: file.name, total_chunks: total,
    });
};

export const uploadImage = async (file, folder = "Products") => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await rhm.post(`/site/media?folder=${folder}`, fd);
    return data.url;
};

/**
 * Cover artwork for an ebook / product card — upload from the computer or pick
 * something already in the media library. Stored as the first product image.
 */
export const CoverImageField = ({ urls = [], onChange, disabled = false,
    testId = "cover-image", label = "Cover image" }) => {
    const fileRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [picking, setPicking] = useState(false);
    const cover = urls[0] || "";

    const setCover = (url) => onChange([url, ...urls.slice(1)].filter(Boolean));

    const pickFile = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            setCover(await uploadImage(file));
            toast.success("Cover uploaded");
        } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    };

    return (
        <div data-testid={testId}>
            <div className="mono text-[10px] tracking-[0.18em] text-zinc-400 mb-1.5">{label}</div>
            <div className="flex items-start gap-4">
                <div className="w-24 h-32 shrink-0 border border-white/10 bg-bg0 flex items-center justify-center overflow-hidden">
                    {cover
                        ? <img src={cover} alt="" className="w-full h-full object-cover"
                            data-testid={`${testId}-thumb`} />
                        : <ImagePlus className="w-5 h-5 text-zinc-600" />}
                </div>
                <div className="space-y-2">
                    <input ref={fileRef} type="file" hidden accept="image/png,image/jpeg,image/webp"
                        data-testid={`${testId}-input`}
                        onChange={(e) => { pickFile(e.target.files[0]); e.target.value = ""; }} />
                    <div className="flex flex-wrap items-center gap-2">
                        <Btn variant="ghost" disabled={disabled || busy} data-testid={`${testId}-upload`}
                            onClick={() => fileRef.current?.click()}>
                            <Upload className="w-3.5 h-3.5 mr-1 inline" />
                            {busy ? "UPLOADING…" : cover ? "REPLACE COVER" : "UPLOAD COVER"}
                        </Btn>
                        <Btn variant="ghost" disabled={disabled} data-testid={`${testId}-library`}
                            onClick={() => setPicking(true)}>MEDIA LIBRARY</Btn>
                        {cover && (
                            <button type="button" disabled={disabled} data-testid={`${testId}-remove`}
                                onClick={() => onChange(urls.slice(1))}
                                className="mono text-[9px] tracking-[0.14em] text-zinc-500 hover:text-red-400">
                                REMOVE
                            </button>
                        )}
                    </div>
                    <p className="text-[11px] text-zinc-500">
                        JPG, PNG or WEBP up to 8 MB. Portrait artwork (3:4) looks best on the shop card.
                    </p>
                </div>
            </div>
            {picking && (
                <MediaLibrary folder="Products" onClose={() => setPicking(false)}
                    onPick={(picked) => { setCover(picked.url); setPicking(false); }} />
            )}
        </div>
    );
};
